/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloGame's round-lifecycle operations against the real
 *     backend via Apollo's imperative client API. Strong for the methods it currently has
 *     (startRound/consumeMatchCredit/getActiveRound/subscribeToRoundStarted — Phase 2 Group B,
 *     plan/plan_frontend_makeover.md, LobbyPanel.tsx's real operations; announceClaim/
 *     believeClaim/liftCup/subscribeToClaimAnnounced/subscribeToCupLifted — Phase 2 Group C,
 *     ControlPanel.tsx's real operations; rollDice/peekDice/subscribeToDiceRolled — Phase 2
 *     Group D, GameSceneCore.tsx's real operations, correcting a previously-fabricated
 *     gameId/turnId/IEntityDiceRoll signature to the real roundId/playerId/
 *     {die1,die2,score,isMeyer,isDouble} shape). makeClaim/makeDecision/getGameSession were
 *     deleted as housekeeping (plan/plan_frontend_makeover.md) — fabricated/idealized
 *     placeholders with zero known callers anywhere in the tree, confirmed after Phase 2
 *     completed. getDiceRanking (frontend-domain-oo P2) reads the server's ranking of the dice,
 *     the rules the client no longer keeps a copy of.
 * O — Good. A new round-lifecycle operation is a new method here plus a new IAdapterApolloGame signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloGame directly.
 * I — Good. Implements the same interface GameSceneCore.tsx/ControlPanel.tsx's real operations need — no
 *     longer silently narrower than the interface promises now that its three fabricated placeholders
 *     (makeClaim/makeDecision/getGameSession) are gone.
 * D — Strong. Takes its domain's GraphQL port and the logger as constructor-injected dependencies (same pattern
 *     as ApolloRoomAdapter.ts) rather than importing a singleton — testable without a real network. Its calls go
 *     through `GraphQLCall`, which answers the port's failure and logs a subscription's error (§28.288).
 *     subscribeToRoundStarted deliberately relays the raw server event unfiltered — the room-scoping
 *     defense-in-depth guard stays in the caller (LobbyPanel.tsx), matching ApolloRoomAdapter.ts's own
 *     subscribe-method precedent.
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import type { IEntityRoundInfo, IEntityActiveRound, IPayloadClaim, IEntityChallenge, IEntityRoll, IEntityDicePair, IEntityDiceRank } from '../../application/ports/apolloShapes';
import type { IAdapterApolloGame, IEntityActiveRoundRestore } from '../../application/ports/IAdapterApolloGame';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';
import { GraphQLCall } from './GraphQLCall';

// ---------------------------------------------------------------------------
// GraphQL operations (Phase 2 Group B). Moved here from LobbyPanel.tsx's inline definitions.
// ---------------------------------------------------------------------------

const START_ROUND_MUTATION = gql`
  mutation ApolloGameAdapter_StartRound($roomId: ID!, $rollerId: ID!) {
    startRound(roomId: $roomId, rollerId: $rollerId) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const GET_ACTIVE_ROUND_QUERY = gql`
  query ApolloGameAdapter_GetActiveRound($roomId: ID!) {
    getActiveRound(roomId: $roomId) {
      roundId rollerId diceRolled
      pendingClaim { claimedScore label challengerId }
      minimumScore
      playerLives { playerId lives }
    }
  }
`;

/** The restore read's shape, taken from the application port so the two cannot drift. */
type TRestoreActiveRound = IEntityActiveRoundRestore | null;

const GET_ACTIVE_ROUND_FOR_RESTORE_QUERY = gql`
  query ApolloGameAdapter_GetActiveRoundForRestore($roomId: ID!) {
    getActiveRound(roomId: $roomId) {
      roundId
      rollerId
      diceRolled
      pendingClaim { claimedScore label challengerId wasBlind }
      minimumScore
      playerLives { playerId lives }
      revealResult {
        losingPlayerId wasBluff claimedScore trueScore die1 die2 newRollerId
      }
    }
  }
`;

const ROLL_DICE_MUTATION = gql`
  mutation ApolloGameAdapter_RollDice($roundId: String!, $playerId: ID!) {
    rollDice(roundId: $roundId, playerId: $playerId) { roundId }
  }
`;

const PEEK_DICE_MUTATION = gql`
  mutation ApolloGameAdapter_PeekDice($roundId: String!, $playerId: ID!) {
    peekDice(roundId: $roundId, playerId: $playerId) {
      die1 die2 score isMeyer isDouble
    }
  }
`;

const DICE_ROLLED_SUB = gql`
  subscription ApolloGameAdapter_DiceRolled($roundId: String!, $roomId: ID) {
    diceRolled(roundId: $roundId, roomId: $roomId) { roundId }
  }
`;

const ROUND_STARTED_SUB = gql`
  subscription ApolloGameAdapter_RoundStarted($roomId: ID!) {
    roundStarted(roomId: $roomId) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const ANNOUNCE_CLAIM_MUTATION = gql`
  mutation ApolloGameAdapter_AnnounceClaim($roundId: String!, $playerId: ID!, $claimedScore: Int!) {
    announceClaim(roundId: $roundId, playerId: $playerId, claimedScore: $claimedScore) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

/**
 * The blind pass. A separate mutation rather than announceClaim + a flag, so that a client
 * which believes it is passing blind — after a peek it has lost track of — is told so, instead
 * of having its claim silently downgraded to a peeked one (§28.62).
 */
const ANNOUNCE_BLIND_MUTATION = gql`
  mutation ApolloGameAdapter_AnnounceBlind($roundId: String!, $playerId: ID!, $claimedScore: Int!) {
    announceBlind(roundId: $roundId, playerId: $playerId, claimedScore: $claimedScore) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

const BELIEVE_CLAIM_MUTATION = gql`
  mutation ApolloGameAdapter_BelieveClaim($roundId: String!, $playerId: ID!, $roomId: String!, $minimumScore: Int!) {
    believeClaim(roundId: $roundId, playerId: $playerId, roomId: $roomId, minimumScore: $minimumScore) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const LIFT_CUP_MUTATION = gql`
  mutation ApolloGameAdapter_LiftCup($roundId: String!, $playerId: ID!) {
    liftCup(roundId: $roundId, playerId: $playerId) {
      roundId roomId trueScore claimedScore losingPlayerId wasBluff newRollerId die1 die2 endsGame loserLivesLeft
    }
  }
`;

const CLAIM_ANNOUNCED_SUB = gql`
  subscription ApolloGameAdapter_ClaimAnnounced($roundId: String!, $roomId: ID) {
    claimAnnounced(roundId: $roundId, roomId: $roomId) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

const CUP_LIFTED_SUB = gql`
  subscription ApolloGameAdapter_CupLifted($roundId: String!, $roomId: ID) {
    cupLifted(roundId: $roundId, roomId: $roomId) {
      roundId roomId trueScore claimedScore losingPlayerId wasBluff newRollerId die1 die2 endsGame loserLivesLeft
    }
  }
`;

/** The rules the client reads instead of keeping a copy: Meyer's 21 combinations, lowest first (frontend-domain-oo P2). */
const DICE_RANKING_QUERY = gql`
  query ApolloGameAdapter_DiceRanking {
    diceRanking { score die1 die2 label isMeyer isDouble rank }
  }
`;

export class ApolloGameAdapter implements IAdapterApolloGame {
  private readonly call: GraphQLCall;

  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(client: IPortGraphQLDomainClient, logger: IServiceLogger) {
    this.call = new GraphQLCall('ApolloGameAdapter', client, logger);
  }

  async rollDice(roundId: string, playerId: PlayerId): Promise<Result<IEntityRoll>> {
    return this.call.required('rollDice', 'rolling dice', (apollo) => apollo.mutate<{ rollDice: IEntityRoll | null }>({
      mutation: ROLL_DICE_MUTATION,
      variables: { roundId, playerId },
    }), (data) => data.rollDice);
  }

  async peekDice(roundId: string, playerId: PlayerId): Promise<Result<IEntityDicePair>> {
    return this.call.required('peekDice', 'peeking dice', (apollo) => apollo.mutate<{ peekDice: IEntityDicePair | null }>({
      mutation: PEEK_DICE_MUTATION,
      variables: { roundId, playerId },
    }), (data) => data.peekDice);
  }

  async startRound(roomId: RoomId, rollerId: PlayerId): Promise<Result<IEntityRoundInfo>> {
    return this.call.required('startRound', 'starting round', (apollo) => apollo.mutate<{ startRound: IEntityRoundInfo | null }>({
      mutation: START_ROUND_MUTATION,
      variables: { roomId, rollerId },
    }), (data) => data.startRound);
  }

  /**
   * MOVED FROM framework/graphql/restoreOperations.ts (2026-08-30), where it was driven by a
   * useLazyQuery in AppRoot. `network-only` is preserved: a restore that reads a cached round
   * would put the player back on a screen the server has already moved past.
   */
  async getActiveRoundForRestore(roomId: RoomId): Promise<Result<TRestoreActiveRound>> {
    return this.call.optional('getActiveRoundForRestore', 'reading the active round', (apollo) => apollo.query<{ getActiveRound: TRestoreActiveRound }>({
      query: GET_ACTIVE_ROUND_FOR_RESTORE_QUERY,
      variables: { roomId },
      fetchPolicy: 'network-only',
    }), (data) => data.getActiveRound);
  }

  async getActiveRound(roomId: RoomId): Promise<Result<IEntityActiveRound | null>> {
    return this.call.optional('getActiveRound', 'fetching active round', (apollo) => apollo.query<{ getActiveRound: IEntityActiveRound | null }>({
      query: GET_ACTIVE_ROUND_QUERY,
      variables: { roomId },
      fetchPolicy: 'network-only',
    }), (data) => data.getActiveRound);
  }

  subscribeToRoundStarted(roomId: RoomId, onEvent: (ev: IEntityRoundInfo) => void): () => void {
    return this.call.subscribe('roundStarted', (apollo) => apollo.subscribe<{ roundStarted: IEntityRoundInfo }>({ query: ROUND_STARTED_SUB, variables: { roomId } }),
      (data) => data.roundStarted, onEvent);
  }

  async announceClaim(roundId: string, playerId: PlayerId, claimedScore: number): Promise<Result<IPayloadClaim>> {
    return this.call.required('announceClaim', 'announcing claim', (apollo) => apollo.mutate<{ announceClaim: IPayloadClaim | null }>({
      mutation: ANNOUNCE_CLAIM_MUTATION,
      variables: { roundId, playerId, claimedScore },
    }), (data) => data.announceClaim);
  }

  async announceBlind(roundId: string, playerId: PlayerId, claimedScore: number): Promise<Result<IPayloadClaim>> {
    return this.call.required('announceBlind', 'announcing blind claim', (apollo) => apollo.mutate<{ announceBlind: IPayloadClaim | null }>({
      mutation: ANNOUNCE_BLIND_MUTATION,
      variables: { roundId, playerId, claimedScore },
    }), (data) => data.announceBlind);
  }

  async believeClaim(roundId: string, playerId: PlayerId, roomId: RoomId, minimumScore: number): Promise<Result<IEntityRoundInfo>> {
    return this.call.required('believeClaim', 'believing claim', (apollo) => apollo.mutate<{ believeClaim: IEntityRoundInfo | null }>({
      mutation: BELIEVE_CLAIM_MUTATION,
      variables: { roundId, playerId, roomId, minimumScore },
    }), (data) => data.believeClaim);
  }

  async liftCup(roundId: string, playerId: PlayerId): Promise<Result<IEntityChallenge>> {
    return this.call.required('liftCup', 'lifting cup', (apollo) => apollo.mutate<{ liftCup: IEntityChallenge | null }>({
      mutation: LIFT_CUP_MUTATION,
      variables: { roundId, playerId },
    }), (data) => data.liftCup);
  }

  subscribeToClaimAnnounced(roundId: string, roomId: RoomId | null, onEvent: (ev: IPayloadClaim) => void): () => void {
    return this.call.subscribe('claimAnnounced', (apollo) => apollo.subscribe<{ claimAnnounced: IPayloadClaim }>({ query: CLAIM_ANNOUNCED_SUB, variables: { roundId, roomId } }),
      (data) => data.claimAnnounced, onEvent);
  }

  subscribeToCupLifted(roundId: string, roomId: RoomId | null, onEvent: (ev: IEntityChallenge) => void): () => void {
    return this.call.subscribe('cupLifted', (apollo) => apollo.subscribe<{ cupLifted: IEntityChallenge }>({ query: CUP_LIFTED_SUB, variables: { roundId, roomId } }),
      (data) => data.cupLifted, onEvent);
  }

  /**
   * `cache-first`, over the client's `network-only` default for queries: the ranking does not change while a page
   * is open, so the server is asked once and every later read is answered from the cache. A read that failed
   * caches nothing, and the next one asks again.
   */
  async getDiceRanking(): Promise<Result<IEntityDiceRank[]>> {
    return this.call.required('getDiceRanking', 'reading the dice ranking', (apollo) => apollo.query<{ diceRanking: IEntityDiceRank[] | null }>({
      query: DICE_RANKING_QUERY,
      fetchPolicy: 'cache-first',
    }), (data) => data.diceRanking);
  }

  subscribeToDiceRolled(roundId: string, roomId: RoomId | null, onEvent: (ev: IEntityRoll) => void): () => void {
    return this.call.subscribe('diceRolled', (apollo) => apollo.subscribe<{ diceRolled: IEntityRoll }>({ query: DICE_ROLLED_SUB, variables: { roundId, roomId } }),
      (data) => data.diceRolled, onEvent);
  }
}
